import { useQuery } from "@apollo/client";
import { Autocomplete, Chip, FormControl, TextField } from "@mui/material";
import { gql } from "generated-graphql";

const TENANT_WASTE_VENDORS_QUERY = gql(`
  query TenantWasteVendors($tenantId: ID!) {
    tenantWasteVendors(tenantId: $tenantId)
  }
`);

interface WasteVendorPickerProps {
  /** Scopes the vendor option list to a single tenant. */
  tenantId?: string;
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Tenant-scoped searchable multi-select of waste vendors (ENG-15174). Options
 * are the distinct vendor names present in the tenant's waste data, so the
 * filter only offers vendors the tenant actually has — mirroring how the
 * facility and tag filters behave. The full list is small (tens of vendors per
 * tenant), so it's fetched once and filtered client-side by the Autocomplete.
 */
export default function WasteVendorPicker({
  tenantId,
  value,
  onChange,
  label,
  disabled,
  onOpen,
  onClose,
}: WasteVendorPickerProps) {
  const { data, loading } = useQuery(TENANT_WASTE_VENDORS_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId,
  });

  const options = data?.tenantWasteVendors ?? [];

  return (
    <FormControl fullWidth>
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={value}
        loading={loading}
        disabled={disabled}
        onChange={(_, newValue) => onChange(newValue)}
        onOpen={onOpen}
        onClose={onClose}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={value.length === 0 ? "Select..." : ""}
            autoComplete="off"
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            }}
          />
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return <Chip key={key} label={option} size="small" {...chipProps} />;
          })
        }
      />
    </FormControl>
  );
}
